'use client'

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './useAuth'
import { supabase } from '@/lib/supabase'
import type {
  UserDocument,
  CreateDocumentInput,
  UpdateDocumentInput
} from '@/types/documents'
import { toast } from 'sonner'
import { useTranslations } from 'next-intl'

export type UserDocumentSummary = Omit<UserDocument, 'content'>

/**
 * Fetch all documents for the current user
 */
export function useDocuments() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['documents', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        return []
      }

      const { data, error } = await supabase
        .from('user_documents')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) {
        throw error
      }

      // Ensure content is properly structured
      return (data || []).map((doc) => ({
        ...doc,
        content: doc.content || { paragraphs: [] }
      })) as UserDocument[]
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false
  })
}

export function useDocumentSummaries() {
  const { user } = useAuth()

  return useQuery({
    queryKey: ['document-summaries', user?.id],
    queryFn: async () => {
      if (!user?.id) return []

      const { data, error } = await supabase
        .from('user_documents')
        .select(
          'id,user_id,title,file_type,file_name,file_size,created_at,updated_at'
        )
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })

      if (error) throw error
      return (data ?? []) as UserDocumentSummary[]
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false
  })
}

/**
 * Create a new document
 */
export function useCreateDocument() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const t = useTranslations('reader')

  return useMutation({
    mutationFn: async (input: CreateDocumentInput) => {
      if (!user?.id) {
        throw new Error('User not logged in')
      }

      const { data, error } = await supabase
        .from('user_documents')
        .insert({
          user_id: user.id,
          title: input.title,
          content: input.content,
          file_type: input.file_type,
          file_name: input.file_name || null,
          file_size: input.file_size || null
        })
        .select()
        .single()

      if (error) {
        throw error
      }

      return data as UserDocument
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', user?.id] })
      queryClient.invalidateQueries({
        queryKey: ['document-summaries', user?.id]
      })
      toast.success(t('documentCreated'))
    },
    onError: (error) => {
      console.error('Document creation error:', error)
      // Extract more detailed error message
      let errorMessage = t('documentCreateError')
      if (error instanceof Error) {
        errorMessage = error.message
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = String((error as { message: unknown }).message)
      }
      toast.error(errorMessage)
    }
  })
}

/**
 * Update a document (title only for now)
 */
export function useUpdateDocument() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const t = useTranslations('reader')

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: UpdateDocumentInput & { id: string }) => {
      if (!user?.id) {
        throw new Error('User not logged in')
      }

      const { data, error } = await supabase
        .from('user_documents')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .eq('user_id', user.id)
        .select()
        .single()

      if (error) {
        throw error
      }

      return data as UserDocument
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', user?.id] })
      queryClient.invalidateQueries({
        queryKey: ['document-summaries', user?.id]
      })
      queryClient.invalidateQueries({ queryKey: ['document'] })
      toast.success(t('documentUpdated'))
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : t('documentUpdateError')
      )
    }
  })
}

/**
 * Delete a document
 */
export function useDeleteDocument() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const t = useTranslations('reader')

  return useMutation({
    mutationFn: async (id: string) => {
      if (!user?.id) {
        throw new Error('User not logged in')
      }

      const { error } = await supabase
        .from('user_documents')
        .delete()
        .eq('id', id)
        .eq('user_id', user.id)

      if (error) {
        throw error
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents', user?.id] })
      queryClient.invalidateQueries({
        queryKey: ['document-summaries', user?.id]
      })
      toast.success(t('documentDeleted'))
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : t('documentDeleteError')
      )
    }
  })
}
